import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../components/Seo";

const FAQ = [
  {
    q: "What is arbitrage betting?",
    a: "Arbitrage betting, also known as 'sure betting' or 'arbing', is the practice of placing wagers on every possible outcome of a sporting event across different sportsbooks such that the combined stakes guarantee a profit regardless of which outcome occurs. It exploits pricing discrepancies between books where the best available price on each side implies a total probability under 100%.",
  },
  {
    q: "How does arbitrage betting work?",
    a: "For a 2-way moneyline event, find the best available American odds on Team A at one book and the best available American odds on Team B at a different book. Convert each to implied probability: for negative odds, abs(odds) / (abs(odds) + 100); for positive odds, 100 / (odds + 100). If the two probabilities sum to less than 1.0, an arbitrage exists. Allocate stake proportional to each implied probability to lock in equal profit on both outcomes.",
  },
  {
    q: "Is arbitrage betting legal in the United States?",
    a: "Yes, arbitrage betting is legal in every US state where sports betting itself is legal. No federal or state statute prohibits placing wagers at multiple legal sportsbooks. However, individual sportsbooks may limit or ban accounts they suspect of arbitrage activity — this is a private contract issue, not a criminal one.",
  },
  {
    q: "How much profit can you make with arbitrage betting?",
    a: "Typical 2-way moneyline arbs yield 0.5% to 3% profit per opportunity, though larger arbs of 5-10%+ appear on lower-liquidity markets like UFC prelims or mid-week MLB games. On a $500 staked arb at 2% margin, guaranteed profit is $10. Volume, not margin, drives arbitrage income.",
  },
  {
    q: "What are the risks of arbitrage betting?",
    a: "Four main risks: (1) line movement — prices change in seconds, so you must place both legs quickly; (2) account limits — sportsbooks throttle or ban frequent arbers; (3) one leg gets voided for a rule violation, leaving you exposed on the other side; (4) calculation errors in stake sizing, which can turn an arb into a partial loss.",
  },
  {
    q: "What sportsbooks are best for arbitrage betting?",
    a: "Best US books for arbitrage are the ones with the widest line variance and slowest to limit accounts: DraftKings, FanDuel, BetMGM, Caesars, BetRivers, PointsBet, Fanatics, and ESPN BET. Regional books like Hard Rock Bet and Unibet also frequently post off-market lines worth comparing.",
  },
  {
    q: "What's the difference between arbitrage and matched betting?",
    a: "Arbitrage uses only real-money prices across books to lock in profit. Matched betting additionally uses free-bet promotions and sign-up bonuses, placing the free-bet side on one book and a real-money lay on an exchange or opposite moneyline elsewhere — capturing the promotion as guaranteed cash. Both are risk-free, but matched betting depends on promotional offers.",
  },
];

export default function ArbitrageBetting() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-background grain relative text-foreground">
      <Seo
        title="Arbitrage Betting · How Sure Bets Work and Where to Find Them"
        path="/arbitrage-betting"
        description="Complete arbitrage betting guide: how sure bets work, the exact math for 2-way moneyline arbs, legality in the US, typical profit margins (0.5-3%), best US sportsbooks for arbing, and how our live scanner finds guaranteed-profit opportunities."
        keywords="arbitrage, arbitrage betting, sure bets, sure betting, arbing, matched betting, sports arbitrage, guaranteed profit betting, arbitrage scanner, +EV betting, line shopping"
        jsonLd={jsonLd}
      />

      <header className="relative z-10 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-8 h-8 border border-[#00D4FF] rotate-45 flex items-center justify-center">
              <span className="-rotate-45 text-[#00D4FF] font-mono text-xs font-bold">M</span>
            </div>
            <div>
              <div className="font-display text-lg leading-none">Martingale</div>
              <div className="overline leading-none mt-1">PLAYBOOK·TERMINAL</div>
            </div>
          </Link>
          <div className="flex gap-2">
            <Link to="/martingale-method" className="px-4 py-2 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-[#00D4FF]">Martingale</Link>
            <Link to="/pricing" className="px-4 py-2 text-xs font-mono uppercase tracking-widest border border-zinc-800 hover:border-[#00D4FF] hover:text-[#00D4FF]">Pricing</Link>
            <Link to="/register" className="px-4 py-2 text-xs font-mono uppercase tracking-widest bg-[#00D4FF] text-black">Start Free Trial</Link>
          </div>
        </div>
      </header>

      <article className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <div className="overline mb-6">GUIDE · ARBITRAGE</div>
        <h1 className="font-display text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
          Arbitrage Betting, Explained
        </h1>
        <p className="text-lg text-zinc-400 leading-relaxed mb-12">
          Arbitrage betting — also called sure betting or arbing — is the one genuinely
          risk-free strategy in sports wagering. When two US sportsbooks disagree
          enough that their combined implied probabilities fall below 100%, placing
          stakes on both sides locks in a guaranteed profit. Here's exactly how it
          works and where to find live opportunities.
        </p>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          What is Arbitrage Betting?
        </h2>
        <p className="text-zinc-300 leading-relaxed mb-4">
          <strong>Arbitrage betting is placing wagers on every possible outcome of a
          sporting event across different sportsbooks such that you are guaranteed a
          profit no matter which outcome occurs.</strong> It exploits the fact that
          different books set lines independently, and occasionally their best prices
          sum to an implied probability below 100% — a mathematical inefficiency the
          arber can capture.
        </p>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          The Math Behind a 2-Way Arb
        </h2>
        <ol className="space-y-3 text-zinc-300 list-decimal list-inside mb-4">
          <li>Find the best available American odds on each side of the market.</li>
          <li>Convert to implied probability. For negative odds:
            <span className="mono text-[#00D4FF]"> implied = abs(odds) / (abs(odds) + 100)</span>.
            For positive odds: <span className="mono text-[#00D4FF]">implied = 100 / (odds + 100)</span>.
          </li>
          <li>If <span className="mono">P(A) + P(B) &lt; 1.0</span>, an arb exists.</li>
          <li>Profit margin: <span className="mono neon-text">(1 − total) / total × 100%</span>.</li>
          <li>Stake sizing: <span className="mono">Stake_A = Total × P(A) / (P(A) + P(B))</span> and <span className="mono">Stake_B = Total − Stake_A</span>.</li>
        </ol>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          Worked Example · $500 Arbitrage
        </h2>
        <div className="border border-zinc-800 my-6 overflow-hidden">
          <table className="w-full mono text-sm">
            <thead className="bg-zinc-900">
              <tr className="text-left">
                <th className="px-4 py-3 overline">SIDE</th>
                <th className="px-4 py-3 overline">BOOK</th>
                <th className="px-4 py-3 overline">PRICE</th>
                <th className="px-4 py-3 overline">IMPLIED %</th>
                <th className="px-4 py-3 overline">STAKE</th>
                <th className="px-4 py-3 overline">TO WIN</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Team A", "DraftKings", "+120", "45.45%", "$231", "$508.20"],
                ["Team B", "FanDuel", "-105", "51.22%", "$269", "$525.19"],
              ].map((r) => (
                <tr key={r[0]} className="border-t border-zinc-800">
                  {r.map((c) => <td key={c} className="px-4 py-3 text-zinc-300">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-zinc-300 leading-relaxed">
          Total implied probability: 96.67%. Profit margin: 3.45%. Guaranteed return on
          $500 staked: approximately $508 regardless of winner. That's <strong className="neon-text">$8 locked profit</strong>.
          Our scanner runs this math live across every US sportsbook every 60 seconds.
        </p>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          Is Arbitrage Betting Legal?
        </h2>
        <p className="text-zinc-300 leading-relaxed mb-6">
          Yes. Arbitrage betting is legal in every US state that has legalized sports
          betting. No federal or state statute prohibits placing wagers at multiple
          legal sportsbooks. However, most books include language in their terms of
          service that allows them to limit or close accounts they suspect of arbing —
          that is a private contract issue, not a legal one.
        </p>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          Where to Find Arbitrage Opportunities
        </h2>
        <ul className="space-y-2 text-zinc-300 mb-6">
          <li className="flex gap-2"><Check size={16} className="text-[#00D4FF] mt-1 shrink-0" /> <strong>Major books</strong>: DraftKings, FanDuel, BetMGM, Caesars, BetRivers.</li>
          <li className="flex gap-2"><Check size={16} className="text-[#00D4FF] mt-1 shrink-0" /> <strong>Regional books</strong>: Hard Rock Bet, Unibet, Fanatics often post off-market lines.</li>
          <li className="flex gap-2"><Check size={16} className="text-[#00D4FF] mt-1 shrink-0" /> <strong>Low-liquidity markets</strong>: UFC prelims, MLB mid-week games, WNBA.</li>
          <li className="flex gap-2"><Check size={16} className="text-[#00D4FF] mt-1 shrink-0" /> <strong>Right after news drops</strong>: injury reports, lineup changes, weather updates.</li>
        </ul>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">
          Risks of Arbitrage Betting
        </h2>
        <ol className="space-y-2 text-zinc-300 list-decimal list-inside mb-6">
          <li><strong>Line movement</strong> — prices change in seconds, so both legs must be placed within 1-2 minutes.</li>
          <li><strong>Account limits</strong> — consistent arbers get throttled or banned by sportsbooks.</li>
          <li><strong>Leg voids</strong> — if one bet gets voided for rules, you're exposed on the other side.</li>
          <li><strong>Stake math errors</strong> — a miscalculated stake can turn an arb into a partial loss.</li>
        </ol>

        <h2 className="font-display text-3xl mt-12 mb-4 text-white">FAQ</h2>
        <div className="space-y-6 mb-10">
          {FAQ.map((f) => (
            <div key={f.q} className="border border-zinc-800 p-5">
              <h3 className="font-display text-xl text-white mb-2">{f.q}</h3>
              <p className="text-sm text-zinc-300 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 border border-[#00D4FF]/40 bg-[#00D4FF]/5 p-8 text-center">
          <div className="overline neon-text mb-3">LIVE SCANNER</div>
          <h2 className="font-display text-3xl mb-4">
            Don't do the math. Let us.
          </h2>
          <p className="text-zinc-300 mb-6 max-w-xl mx-auto">
            Our arbitrage scanner checks every US sportsbook every minute and serves
            you the opportunities with stake plans ready to place. 3 days free, then
            $9.99/week.
          </p>
          <Link
            to="/register"
            data-testid="arb-cta-register"
            className="inline-flex items-center gap-2 px-6 py-4 bg-[#00D4FF] text-black font-mono uppercase tracking-widest text-xs"
          >
            Start 3 Free Days <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </article>

      <footer className="relative z-10 border-t border-zinc-800 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-xs mono text-zinc-500 text-center">
          MARTINGALE PLAYBOOK · GAMBLE RESPONSIBLY · 1-800-GAMBLER
        </div>
      </footer>
    </div>
  );
}
